"use client";

import { c as _c } from "react/compiler-runtime";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "cn";
import { X } from "lucide-react";
import type * as React from "react";
import { DialogOverlay } from "./Dialog";
import { keepLayerOpenForPopup, PortalContainerContext, usePortalContainerRef } from "./PortalContainer";

/** A modal panel that slides in from the right edge. */
function Sheet(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <DialogPrimitive.Root data-slot="sheet" {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function SheetClose(t0) {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <DialogPrimitive.Close data-slot="sheet-close" {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}

/**
 * The sliding panel. Popups opened inside it, such as a Combobox list, render
 * within it so they keep receiving the mouse while the sheet turns pointer
 * events off outside itself.
 */
function SheetContent(t0) {
  const $ = _c(19);
  let className;
  let onEscapeKeyDown;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      onEscapeKeyDown,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onEscapeKeyDown;
    $[3] = props;
    $[4] = ref;
  } else {
    className = $[1];
    onEscapeKeyDown = $[2];
    props = $[3];
    ref = $[4];
  }
  const [container, contentRef] = usePortalContainerRef(ref);
  let t1;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <DialogOverlay data-slot="sheet-overlay" />;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== className) {
    t2 = cn("fixed inset-y-0 right-0 z-50 flex h-full w-full flex-col overflow-y-auto md:max-w-2xl", "border-border bg-card text-foreground shadow-2xl outline-none md:border-l", "data-[state=open]:animate-in data-[state=open]:slide-in-from-right", "data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right", "duration-200", className);
    $[6] = className;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== container || $[9] !== onEscapeKeyDown) {
    t3 = keepLayerOpenForPopup(container, onEscapeKeyDown);
    $[8] = container;
    $[9] = onEscapeKeyDown;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== contentRef || $[12] !== props || $[13] !== t2 || $[14] !== t3) {
    t4 = <DialogPrimitive.Content ref={contentRef} data-slot="sheet-content" className={t2} {...props} onEscapeKeyDown={t3} />;
    $[11] = contentRef;
    $[12] = props;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== container || $[17] !== t4) {
    t5 = <DialogPrimitive.Portal>{t1}<PortalContainerContext.Provider value={container}>{t4}</PortalContainerContext.Provider></DialogPrimitive.Portal>;
    $[16] = container;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
}

/** Title and description on the left, the close button on the right. */
function SheetHeader(t0) {
  const $ = _c(13);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("flex shrink-0 items-start justify-between gap-3", "px-6 py-4", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children) {
    t2 = <div className="flex min-w-0 flex-1 flex-col gap-0.5">{children}</div>;
    $[6] = children;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <DialogPrimitive.Close className={cn("flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-sm", "text-foreground hover:bg-accent", "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50")}><X aria-hidden="true" className="size-4" /><span className="sr-only">Close</span></DialogPrimitive.Close>;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== props || $[10] !== t1 || $[11] !== t2) {
    t4 = <div data-slot="sheet-header" className={t1} {...props}>{t2}{t3}</div>;
    $[9] = props;
    $[10] = t1;
    $[11] = t2;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
function SheetTitle(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("heading-md text-foreground", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <DialogPrimitive.Title data-slot="sheet-title" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SheetDescription(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("body-sm text-muted-foreground", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <DialogPrimitive.Description data-slot="sheet-description" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export { Sheet, SheetClose, SheetContent, SheetDescription, SheetHeader, SheetTitle };